import { Logger, LogSender } from '../../../logger';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import { StreamingEventBus } from '../../abstract/StreamingEventBus';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingSubscription, {
  StreamingSubscriptionEventHandler,
} from '../../abstract/StreamingSubscription';
import {
  getNewsSubscribeRequest,
  NewsAddResponse,
  NewsAddResponseRaw,
  NewsChangeResponse,
  NewsChangeResponseRaw,
  NewsDeleteResponse,
  NewsDeleteResponseRaw,
  NewsRequestPayload,
  NewsSnapshotResponse,
  NewsSnapshotResponseRaw,
  NewsSubscribeRequest,
  NewsSubscribeResponse,
} from './Messages';

export type NewsOnSubscribe = (
  msg: NewsSubscribeResponse,
  err: StreamingError
) => void;
export type NewsOnSnapshot = (
  msg: NewsSnapshotResponse,
  err: StreamingError
) => void;
export type NewsOnAdd = (msg: NewsAddResponse, err: StreamingError) => void;
export type NewsOnChange = (
  msg: NewsChangeResponse,
  err: StreamingError
) => void;
export type NewsOnDelete = (
  msg: NewsDeleteResponse,
  err: StreamingError
) => void;

export default class NewsSubscription extends StreamingSubscription<
  NewsRequestPayload,
  NewsSubscribeRequest
> {
  private _fids: string[] = [];
  private _timezone: string;
  private _history: string;
  private _fromnumber: number;
  private _tonumber: number;
  private _filter: string;

  public get subscribed(): NewsRequestPayload {
    return {
      fids: this._fids,
      timezone: this._timezone,
      history: this._history,
      fromnumber: this._fromnumber,
      tonumber: this._tonumber,
      filter: this._filter,
    };
  }

  public get SubscribeRequest(): NewsSubscribeRequest {
    return getNewsSubscribeRequest(this.id, [this.subscribed]);
  }

  public readonly on: {
    [DoryMessageType.SUBSCRIBE]: StreamingSubscriptionEventHandler<NewsSubscribeResponse>;
    [DoryMessageType.SNAPSHOT]: StreamingSubscriptionEventHandler<NewsSnapshotResponse>;
    [DoryMessageType.ADD]: StreamingSubscriptionEventHandler<NewsAddResponse>;
    [DoryMessageType.CHANGE]: StreamingSubscriptionEventHandler<NewsChangeResponse>;
    [DoryMessageType.DELETE]: StreamingSubscriptionEventHandler<NewsDeleteResponse>;
  } = {
    /**
     * The handler for "subscribe" has to be called when:
     * - a message of with messageType "subscribe" is received and channelId equal to this.id
     *
     * The handler is stopped when:
     * - it's called
     *
     * Attach an error object if:
     * - payload.status !== 200
     */
    [DoryMessageType.SUBSCRIBE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SUBSCRIBE].isAttached = true;
        this.on[DoryMessageType.SUBSCRIBE].detach =
          this.eventBus.once<NewsSubscribeResponse>(
            DoryServiceType.NEWS,
            DoryMessageType.SUBSCRIBE,
            (data) => {
              let err: StreamingError = undefined;
              if (data.payload.status !== 200) {
                err = new StreamingError(
                  'Streaming.News.NewsSubscription.onSubscribe',
                  StreamingErrorCodes.INVALID_REQUEST,
                  'Failed to subscribe to news'
                );
              }

              this.on[DoryMessageType.SUBSCRIBE].handler(data, err);
              this.on[DoryMessageType.SUBSCRIBE].isAttached = false;
              this.stopTimeout();
            },
            (data) => data.header.channelId === this.id
          );
      },
    },
    /**
     * The handler for "snapshot" has to be called when:
     * - a message of with messageType "snapshot" is received and channelId equal to this.id
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.SNAPSHOT]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SNAPSHOT].isAttached = true;
        this.on[DoryMessageType.SNAPSHOT].detach =
          this.eventBus.on<NewsSnapshotResponseRaw>(
            DoryServiceType.NEWS,
            DoryMessageType.SNAPSHOT,
            (data) => {
              if (this.id === data.header.channelId) {
                this.on[DoryMessageType.SNAPSHOT].handler(
                  this.parseResponse(data),
                  undefined
                );
              }
            }
          );
      },
    },
    /**
     * The handler for "add" has to be called when:
     * - a message of with messageType "add" is received and channelId equal to this.id
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.ADD]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.ADD].isAttached = true;
        this.on[DoryMessageType.ADD].detach =
          this.eventBus.on<NewsAddResponseRaw>(
            DoryServiceType.NEWS,
            DoryMessageType.ADD,
            (data) => {
              if (this.id === data.header.channelId) {
                this.on[DoryMessageType.ADD].handler(
                  this.parseResponse(data),
                  undefined
                );
              }
            }
          );
      },
    },
    /**
     * The handler for "change" has to be called when:
     * - a message of with messageType "change" is received and channelId equal to this.id
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.CHANGE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.CHANGE].isAttached = true;
        this.on[DoryMessageType.CHANGE].detach =
          this.eventBus.on<NewsChangeResponseRaw>(
            DoryServiceType.NEWS,
            DoryMessageType.CHANGE,
            (data) => {
              if (this.id === data.header.channelId) {
                this.on[DoryMessageType.CHANGE].handler(
                  this.parseResponse(data),
                  undefined
                );
              }
            }
          );
      },
    },
    /**
     * The handler for "delete" has to be called when:
     * - a message of with messageType "delete" is received and channelId equal to this.id
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.DELETE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.DELETE].isAttached = true;
        this.on[DoryMessageType.DELETE].detach =
          this.eventBus.on<NewsDeleteResponseRaw>(
            DoryServiceType.NEWS,
            DoryMessageType.DELETE,
            (data) => {
              if (this.id === data.header.channelId) {
                this.on[DoryMessageType.DELETE].handler(
                  this.parseResponse(data),
                  undefined
                );
              }
            }
          );
      },
    },
  };

  constructor(
    id: string,
    eventBus: StreamingEventBus,
    fids: string[],
    timezone: string,
    history: string,
    fromnumber: number,
    tonumber: number,
    filter: string,
    onSubscribe: NewsOnSubscribe,
    onSnapshot: NewsOnSnapshot,
    onAdd: NewsOnAdd,
    onChange: NewsOnChange,
    onDelete: NewsOnDelete
  ) {
    super(id, eventBus);
    this._fids = fids;
    this._timezone = timezone;
    this._history = history;
    this._fromnumber = fromnumber;
    this._tonumber = tonumber;
    this._filter = filter;

    this.on[DoryMessageType.SUBSCRIBE].handler = onSubscribe;
    this.on[DoryMessageType.SNAPSHOT].handler = onSnapshot;
    this.on[DoryMessageType.ADD].handler = onAdd;
    this.on[DoryMessageType.CHANGE].handler = onChange;
    this.on[DoryMessageType.DELETE].handler = onDelete;
  }

  public start(timeout: number): void {
    // Attach all event listeners
    this.on[DoryMessageType.SUBSCRIBE].attach();
    this.on[DoryMessageType.SNAPSHOT].attach();
    this.on[DoryMessageType.ADD].attach();
    this.on[DoryMessageType.CHANGE].attach();
    this.on[DoryMessageType.DELETE].attach();
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.News.NewsSubscription.start',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public restart(timeout: number): void {
    // Attach all event listeners that might be detached
    if (!this.on[DoryMessageType.SUBSCRIBE].isAttached) {
      this.on[DoryMessageType.SUBSCRIBE].attach();
    }
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.News.NewsSubscription.restart',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public stop(): void {
    // Stop all event listeners
    if (this.on[DoryMessageType.SUBSCRIBE].detach) {
      this.on[DoryMessageType.SUBSCRIBE].detach();
      Logger.silly(LogSender.STREAMING_NEWS, 'Detached Subscribe listener');
    }
    if (this.on[DoryMessageType.SNAPSHOT].detach) {
      this.on[DoryMessageType.SNAPSHOT].detach();
      Logger.silly(LogSender.STREAMING_NEWS, 'Detached Snapshot listener');
    }
    if (this.on[DoryMessageType.ADD].detach) {
      this.on[DoryMessageType.ADD].detach();
      Logger.silly(LogSender.STREAMING_NEWS, 'Detached Add listener');
    }
    if (this.on[DoryMessageType.CHANGE].detach) {
      this.on[DoryMessageType.CHANGE].detach();
      Logger.silly(LogSender.STREAMING_NEWS, 'Detached Change listener');
    }
    if (this.on[DoryMessageType.DELETE].detach) {
      this.on[DoryMessageType.DELETE].detach();
      Logger.silly(LogSender.STREAMING_NEWS, 'Detached Delete listener');
    }
    // Stop pending timeout
    this.stopTimeout();
  }

  /**
   * Parse the message sent from the WebSocket into a an Object
   *
   * @param response Raw server response
   * @returns Parsed response
   */
  private parseResponse(
    response:
      | NewsAddResponseRaw
      | NewsChangeResponseRaw
      | NewsSnapshotResponseRaw
      | NewsDeleteResponseRaw
  ):
    | NewsAddResponse
    | NewsChangeResponse
    | NewsSnapshotResponse
    | NewsDeleteResponse {
    const fids = response.payload[0].fids;
    const news = response.payload[0].value.map(
      (item: Array<string | string[]>) => {
        const data: {
          [key: string]: string | string[];
        } = {};
        for (let i = 0; i < fids.length; i++) {
          data[fids[i]] = item[i];
        }

        return data;
      }
    );

    return {
      header: response.header,
      payload: {
        fids,
        news,
      },
    };
  }
}
